import React, { useState, useEffect, useRef } from 'react';
import '../styles/modes/compareMode.css';
import ModeHelpTooltip from './ModeHelpTooltip';
import PathInfo from './PathInfo';
import ComparisonResult from './ComparisonResult';
import { 
  calculatePathDetails,
  calculatePathDuration
} from './utils';

export default function CompareMode({ locations, startId, autoPath, customPath, vehicleCount = 2, connections, customPathMetadata, autoSolveResult, demand = 0, modeCapacities = {} }) {
  const [autoPathInfo, setAutoPathInfo] = useState(null);
  const [customPathInfo, setCustomPathInfo] = useState(null);
  const [pathDifference, setPathDifference] = useState(null);
  const [autoSegments, setAutoSegments] = useState([]);
  const [customSegments, setCustomSegments] = useState([]);
  const [expandedDetails, setExpandedDetails] = useState(false);
  // Help tooltip state
  const [showHelp, setShowHelp] = useState(false);
  
  // Use refs to track previous paths and avoid processing the same data repeatedly
  const prevAutoPathRef = useRef(null);
  const prevCustomPathRef = useRef(null);

  // Toggle help visibility
  const toggleHelp = () => setShowHelp(!showHelp);
  

  
  // Calculate path information
  useEffect(() => {
    // Check if paths have changed; skip processing if unchanged
    const autoPathStr = JSON.stringify(autoPath);
    const customPathStr = JSON.stringify(customPath);
    
    if (autoPathStr === prevAutoPathRef.current && 
        customPathStr === prevCustomPathRef.current) {
      return; // Data hasn't changed, skip update
    }
    
    // Update reference values
    prevAutoPathRef.current = autoPathStr;
    prevCustomPathRef.current = customPathStr;
    

    
    // Calculate auto path information
    if (autoPath && (Array.isArray(autoPath) && autoPath.length > 1)) {
      const { distance, segments } = calculatePathDetails(autoPath, locations);
      
      // Directly use path array length, including all points (including duplicates)
      let autoPointCount = autoPath.length;
      
      // Use duration from autoSolveResult if available (capacity-adjusted), otherwise use calculated distance
      const autoDuration = autoSolveResult?.totalTime || distance;
      
      setAutoPathInfo({
        distance: autoDuration,  // In MTO, this represents duration (hours)
        pointCount: autoPointCount,
        complete: autoPath.length > 0 && autoPath[0] === autoPath[autoPath.length - 1]
      });
      setAutoSegments(segments);
    } else {
      setAutoPathInfo(null);
      setAutoSegments([]);
    }

    // Calculate custom path information
    if (customPath && (Array.isArray(customPath) && customPath.length > 1)) {
      const { distance, segments } = calculatePathDetails(customPath, locations);
      
      // Directly use path array length, including all points (including duplicates)
      let customPointCount = customPath.length;
      
      // Determine which duration to use based on demand (capacity constraint mode)
      let customDuration;
      if (demand > 0 && customPathMetadata?.totalDuration) {
        // Capacity constraint mode: use capacity-adjusted duration from metadata
        customDuration = customPathMetadata.totalDuration;
      } else {
        // Standard mode: calculate base duration from connections
        customDuration = calculatePathDuration(customPath, locations, connections);
      }
      
      setCustomPathInfo({
        distance: customDuration,  // In MTO, this represents duration (hours)
        pointCount: customPointCount,
        complete: customPath.length > 0 && customPath[0] === customPath[customPath.length - 1]
      });
      setCustomSegments(segments);
    } else {
      setCustomPathInfo(null);
      setCustomSegments([]);
    }
  }, [autoPath, customPath, locations, customPathMetadata, autoSolveResult, connections, demand]);

  // Calculate path difference
  useEffect(() => {
    // Only calculate difference if both paths have valid data
    if (autoPathInfo && customPathInfo && 
        typeof autoPathInfo.distance === 'number' && 
        typeof customPathInfo.distance === 'number' &&
        autoPathInfo.distance > 0) {
      
      const distanceDiff = customPathInfo.distance - autoPathInfo.distance;
      const percentDiff = (distanceDiff / autoPathInfo.distance) * 100;
      

      
      // Consider paths equal if difference is very small (< 0.01 km = 10 meters)
      const TOLERANCE = 0.01;
      const isEffectivelyEqual = Math.abs(distanceDiff) < TOLERANCE;
      
      setPathDifference({
        absoluteDiff: Math.abs(distanceDiff),
        percentDiff: percentDiff,
        isCustomBetter: isEffectivelyEqual ? null : distanceDiff < 0, // null means equal
        isEffectivelyEqual: isEffectivelyEqual
      });
    } else {
      setPathDifference(null);
    }
  }, [autoPathInfo, customPathInfo]);
  
  // Toggle detailed comparison display
  const toggleDetails = () => {
    setExpandedDetails(!expandedDetails);
  };

  return (
    <div className="compare-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Compare Mode</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Compare Mode"
          >
            ?
          </button>
        </div>
        <p>Compare performance differences between auto and custom paths.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}
      
      <div className="comparison-panel">
        <h4>Path Comparison</h4>
        
        {(!autoPath || !customPath) && (
          <div className="empty-paths-message">
            <p>Please create paths in "Auto Find Path" and "Custom Path" modes first, then compare.</p>
          </div>
        )}
        
        {(autoPath && customPath) && (
          <div className="paths-comparison">
            <div className="path-info-container">
              {/* Auto Path Information */}
              <PathInfo 
                pathInfo={autoPathInfo} 
                path={autoPath} 
                locations={locations} 
                title="Auto Path (Algorithm Solution)" 
                className="auto" 
                isEmpty="auto"
                connections={connections}
              />
              
              {/* Custom Path Information */}
              <PathInfo 
                pathInfo={customPathInfo} 
                path={customPath} 
                locations={locations} 
                title="Custom Path (Manual Planning)" 
                className="custom" 
                isEmpty="custom"
                connections={connections}
              />
            </div>
            
            {/* Comparison Results */}
            <ComparisonResult 
              pathDifference={pathDifference}
              autoPathInfo={autoPathInfo}
              customPathInfo={customPathInfo}
              expandedDetails={expandedDetails}
              toggleDetails={toggleDetails}
              autoSegments={autoSegments}
              customSegments={customSegments}
              autoPath={autoPath}
              customPath={customPath}
              locations={locations}
              connections={connections}
            />
          </div>
        )}
      </div>
    </div>
  );
} 